import { useState } from "react";
import { StatusPill } from "@/components/shared/StatusPill";
import { DocumentPanel } from "@/components/shared/DocumentPanel";
import { Loading } from "@/components/shared/Loading";
import { money } from "@/lib/format";
import { useCustomer, useCustomerDocuments, useCustomerDocumentChecklist } from "@/api/hooks";
import { FileText, ChevronRight, ShieldCheck, ShieldAlert } from "lucide-react";

// Aug 2026 Finance dashboard scope pass (round 2) — the SAME complete
// customer-information + documents workspace for both Finance case types:
// the Credit Check / Finance Review case (FinanceOrderReview.jsx, from
// Sales) and the Invoice Clearance / Final Invoice Preparation case
// (Pipeline.jsx invoice-clearance dialog, from Operations). Built once here
// and embedded in both places rather than duplicated, per the "reuse, don't
// duplicate customer or document data" instruction.
//
// Every field rendered below reads from data that already exists elsewhere
// in the system (GET /customers/{cid}, GET /customers/{cid}/documents, GET
// /customers/{cid}/document-checklist) — nothing here re-asks Finance to
// enter information, and nothing here is fabricated. Where a field the
// product request explicitly calls out (PAN / CIN / Aadhaar as standalone
// customer profile fields, as opposed to uploaded documents) has no real
// backing value anywhere in this system, it is simply not rendered — the
// document-checklist endpoint already tracks Aadhaar Card / PAN Card / CIN
// Certificate as documents (Received/Missing), which is the real, honest
// signal Finance can act on, so that's what's shown instead.
export function FinanceCustomerContext({ clientId, defaultOpen = true }) {
  const { data: c } = useCustomer(clientId);
  const { data: docsData } = useCustomerDocuments(clientId);
  const { data: checklist } = useCustomerDocumentChecklist(clientId);
  const [open, setOpen] = useState(defaultOpen);
  const [activeDoc, setActiveDoc] = useState(null);
  const [docOpen, setDocOpen] = useState(false);

  if (!clientId) return null;
  if (!c) return <Loading />;

  const contacts = c.contacts || [];
  const documents = docsData?.documents || [];
  const rows = checklist?.checklist || [];
  const kycRows = rows.filter((r) => r.kind === "KYC");
  const tradeRows = rows.filter((r) => r.kind === "TRADE");
  const previousInvoices = c.invoices || [];
  const openDoc = (d) => { if (!d) return; setActiveDoc(d); setDocOpen(true); };
  const findDoc = (docId) => documents.find((d) => d.id === docId);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="finance-customer-context">
      <button className="w-full flex items-center justify-between" onClick={() => setOpen((v) => !v)} data-testid="fcc-toggle">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Customer information &amp; documents</div>
        <ChevronRight className={`h-3.5 w-3.5 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="mt-3 space-y-4">
          {/* Company + billing */}
          <div>
            <div className="text-sm font-display font-semibold text-slate-900">{c.name}</div>
            <div className="text-[12px] text-slate-500">{[c.id, c.industry, c.location].filter(Boolean).join(" · ")}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2.5">
              <Field label="Customer type" value={c.financeStatus || "—"} />
              <Field label="TRN / Tax registration" value={c.trn || "—"} />
              <Field label="Trade licence" value={c.tradeLicenseNumber || "—"} />
              <Field label="Billing email" value={c.billingEmail || "—"} />
              <Field label="Customer since" value={c.since || "—"} />
              <Field label="Sales owner" value={c.salesOwner || "—"} />
            </div>
          </div>

          {/* Credit & exposure — existing figures, not re-entered */}
          <div className="pt-3 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Credit &amp; exposure</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Credit limit" value={c.creditLimit != null ? (c.creditLimit ? money(c.creditLimit, c.currency) : "Cash account") : "—"} />
              <Field label="Payment terms" value={c.paymentTerms || "—"} />
              <Field label="Current exposure" value={c.exposure != null ? money(c.exposure, c.currency) : "—"} />
              <Field label="Overdue" value={c.overdue > 0 ? money(c.overdue, c.currency) : "None"} danger={c.overdue > 0} />
              <Field label="Lifetime revenue" value={c.lifetimeRevenue != null ? money(c.lifetimeRevenue, c.currency) : "—"} />
              <Field label="Open jobs" value={c.openJobs ?? "—"} />
            </div>
          </div>

          {/* Contacts */}
          {contacts.length > 0 && (
            <div className="pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Contacts</div>
              <div className="space-y-1.5">
                {contacts.map((ct) => (
                  <div key={ct.email || ct.id} className="text-[12.5px] text-slate-700">
                    <span className="font-medium">{ct.name}</span>
                    {ct.role && <span className="text-slate-400"> · {ct.role}</span>}
                    <span className="text-slate-400"> · {[ct.phone || ct.whatsapp, ct.email].filter(Boolean).join(" · ") || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Previous trade / transaction history — real invoice history, not fabricated */}
          {previousInvoices.length > 0 && (
            <div className="pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Previous trade / invoice history</div>
              <div className="space-y-1">
                {previousInvoices.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between text-[12.5px]">
                    <span className="font-mono-num text-slate-600">{inv.id} · Job {inv.jobId}</span>
                    <span className="flex items-center gap-2"><span className="font-mono-num text-slate-700">{money(inv.amount, inv.currency)}</span><StatusPill status={inv.status} /></span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* KYC checklist — Received/Missing, sourced from real onboarding + uploaded docs */}
          {kycRows.length > 0 && (
            <div className="pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">KYC &amp; onboarding documents</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {kycRows.map((r) => (
                  <button key={r.name} onClick={() => openDoc(findDoc(r.documentId))} disabled={!r.documentId}
                    className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-left text-[12px] ${r.submitted ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100" : "border-amber-200 bg-amber-50 text-amber-800"} ${r.documentId ? "cursor-pointer" : "cursor-default"}`}
                    data-testid={`fcc-kyc-${r.name.replace(/\s+/g, "-")}`}>
                    {r.submitted ? <ShieldCheck className="h-3.5 w-3.5 shrink-0" /> : <ShieldAlert className="h-3.5 w-3.5 shrink-0" />}
                    <span className="truncate flex-1">{r.name}</span>
                    <span className="text-[10px] font-semibold uppercase shrink-0">{r.status}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {tradeRows.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Trade document checklist</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {tradeRows.map((r) => (
                  <button key={r.name} onClick={() => openDoc(findDoc(r.documentId))} disabled={!r.documentId}
                    className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-left text-[12px] ${r.submitted ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100" : "border-amber-200 bg-amber-50 text-amber-800"} ${r.documentId ? "cursor-pointer" : "cursor-default"}`}
                    data-testid={`fcc-trade-${r.name.replace(/\s+/g, "-")}`}>
                    {r.submitted ? <ShieldCheck className="h-3.5 w-3.5 shrink-0" /> : <ShieldAlert className="h-3.5 w-3.5 shrink-0" />}
                    <span className="truncate flex-1">{r.name}</span>
                    <span className="text-[10px] font-semibold uppercase shrink-0">{r.status}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Full document list — same records, opened via the shared DocumentPanel viewer */}
          <div className="pt-3 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">All documents on file · {documents.length}</div>
            {documents.length === 0 ? (
              <div className="text-[12.5px] text-slate-400">No documents uploaded for this customer yet.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {documents.map((d) => (
                  <button key={d.id} onClick={() => openDoc(d)} data-testid={`fcc-doc-${d.id}`}
                    className="w-full text-left py-2 flex items-center gap-2.5 hover:bg-slate-50 transition-colors rounded-lg px-1">
                    <FileText className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-medium text-slate-800 truncate">{d.name}</div>
                      <div className="text-[11px] text-slate-400">{d.type} · {[d.source, d.uploadedBy].filter(Boolean).join(" · ")} · {d.date}</div>
                    </div>
                    <StatusPill status={d.review} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <DocumentPanel doc={activeDoc} open={docOpen} onOpenChange={setDocOpen} />
    </div>
  );
}

const Field = ({ label, value, danger }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
    <div className={`text-[13px] font-medium font-mono-num mt-0.5 ${danger ? "text-red-600" : "text-slate-800"}`}>{value}</div>
  </div>
);
